import { callEdgeFunction, EdgeFunctionError } from './edgeClient';

// ============================================
// SESSION PERSISTENCE FUNCTIONS
// ============================================

// Shared failure shape: HTTP errors carry the server's message; anything else (no
// response, unparseable body, missing input) is reported as a network error as before.
const failure = (error, fallback, httpFallback) => {
  if (error instanceof EdgeFunctionError) {
    return {
      success: false,
      status: error.status,
      message: error.body?.error || `${httpFallback}: ${error.status}`
    };
  }
  return {
    success: false,
    message: error.message || fallback,
    isNetworkError: true
  };
};

// Save scan progress to server for session persistence.
// `keepalive` lets the request outlive the page (used when flushing on pagehide).
export const saveScanProgress = async (sessionId, scannedItems, { keepalive = false } = {}) => {
  try {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    const responseData = await callEdgeFunction('save-scan-progress', { body: { sessionId, scannedItems }, keepalive });

    return {
      success: true,
      updatedAt: responseData.updatedAt,
      message: 'Progress saved successfully'
    };
  } catch (error) {
    console.error('Error saving scan progress:', error);
    return failure(error, 'Failed to save scan progress', 'Failed to save progress');
  }
};

// Get session data for restoration
export const getSession = async (sessionId) => {
  try {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    const responseData = await callEdgeFunction('get-session', { body: { sessionId } });

    return {
      success: true,
      session: responseData.session,
      message: 'Session retrieved successfully'
    };
  } catch (error) {
    console.error('Error getting session:', error);
    return failure(error, 'Failed to get session', 'Failed to get session');
  }
};

// Abandon a session (mark as abandoned in database)
export const abandonSession = async (sessionId) => {
  try {
    if (!sessionId) {
      // No session to abandon - not an error
      return { success: true, message: 'No session to abandon' };
    }

    const responseData = await callEdgeFunction('abandon-session', { body: { sessionId } });

    return {
      success: true,
      message: responseData.message || 'Session abandoned successfully'
    };
  } catch (error) {
    console.error('Error abandoning session:', error);
    return failure(error, 'Failed to abandon session', 'Failed to abandon session');
  }
};
